import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  Divider,
  Button,
  Pagination,
} from '@mui/material';
import {
  Book,
  Quiz,
  Assignment,
  CheckCircle,
  PlayArrow,
  TrendingUp,
  EmojiEvents,
  AutoAwesome,
} from '@mui/icons-material';
import activityService, { ActivityEvent, ActivityFeedResponse } from '../../services/activityService';
import { format, formatDistanceToNow } from 'date-fns';

interface ActivityFeedProps {
  studentId?: number;
  topicId?: number;
  category?: string;
  pageSize?: number;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  studentId,
  topicId,
  category,
  pageSize = 20,
}) => {
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadActivities();
  }, [page, category, topicId]);

  const loadActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      let response: ActivityFeedResponse;
      if (topicId) {
        const events = await activityService.getTopicActivity(topicId);
        setActivities(events);
        setTotalPages(1);
      } else if (category) {
        response = await activityService.getActivityFeedByCategory(category, page, pageSize);
        setActivities(response.content);
        setTotalPages(response.totalPages);
      } else {
        response = await activityService.getActivityFeed(page, pageSize);
        setActivities(response.content);
        setTotalPages(response.totalPages);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType: string, category: string) => {
    if (category === 'ACHIEVEMENT') return <EmojiEvents />;
    if (category === 'QUIZ') return <Quiz />;
    if (category === 'ASSESSMENT') return <Assignment />;
    if (category === 'AI_INTERACTION') return <AutoAwesome />;
    if (eventType.includes('COMPLETED')) return <CheckCircle />;
    if (eventType.includes('STARTED')) return <PlayArrow />;
    return <Book />;
  };

  const getEventColor = (category: string) => {
    switch (category) {
      case 'ACHIEVEMENT':
        return 'success';
      case 'QUIZ':
        return 'primary';
      case 'ASSESSMENT':
        return 'warning';
      case 'AI_INTERACTION':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatEventDescription = (event: ActivityEvent) => {
    const eventType = event.eventType;
    const category = event.eventCategory;

    if (eventType === 'TOPIC_COMPLETED') {
      return 'Completed topic';
    }
    if (eventType === 'TOPIC_STARTED') {
      return 'Started learning topic';
    }
    if (eventType === 'QUIZ_SUBMITTED') {
      const score = event.eventData?.score;
      return `Submitted quiz${score !== undefined ? ` (Score: ${score}%)` : ''}`;
    }
    if (eventType === 'QUIZ_STARTED') {
      return 'Started quiz';
    }
    if (eventType === 'ASSESSMENT_SUBMITTED') {
      const score = event.eventData?.score;
      return `Submitted assessment${score !== undefined ? ` (Score: ${score}%)` : ''}`;
    }
    if (eventType === 'ASSESSMENT_STARTED') {
      return 'Started assessment';
    }
    if (eventType === 'BADGE_EARNED') {
      return `Earned badge: ${event.eventData?.badgeName || 'Unknown'}`;
    }
    if (eventType === 'CERTIFICATE_EARNED') {
      return `Earned certificate: ${event.eventData?.certificateName || 'Unknown'}`;
    }
    if (eventType === 'RANK_UP') {
      return `Ranked up to ${event.eventData?.rankName || 'Unknown'}`;
    }

    return eventType.replace(/_/g, ' ').toLowerCase();
  };

  if (loading && activities.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Activity Feed
          </Typography>
          <Alert severity="info">No activities found.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Activity Feed
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Track your learning progress and achievements.
        </Typography>

        <List>
          {activities.map((activity, index) => (
            <React.Fragment key={activity.id}>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: `${getEventColor(activity.eventCategory)}.main` }}>
                    {getEventIcon(activity.eventType, activity.eventCategory)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={formatEventDescription(activity)}
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      </Typography>
                      {activity.durationSeconds && (
                        <Chip
                          label={`${Math.floor(activity.durationSeconds / 60)} min`}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  }
                />
                <Chip
                  label={activity.eventCategory}
                  size="small"
                  color={getEventColor(activity.eventCategory) as any}
                />
              </ListItem>
              {index < activities.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, value) => setPage(value - 1)}
              color="primary"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
